import { computed, ComputedRef, ref, watch } from "vue";
import { DatepickerTypeEnum } from "../enums";
import { getMaskOptions } from "../constants";
import { modifyDate } from "../utils";

type UseDatepickerInputOptions = {
  /**Тип пикера */
  type: DatepickerTypeEnum;
  /**Установить дату */
  setDate: (date: Date | undefined) => void;
  /**Текущее значение даты */
  date: ComputedRef<Date | undefined>;
};

export const useDatepickerInput = (opts: UseDatepickerInputOptions) => {
  /**Опции маски */
  const maskOptions = getMaskOptions(opts.type);
  /**Длинна не маскированного значения */
  const unMasklength = computed(() =>
    maskOptions.blocks
      ? Object.values(maskOptions.blocks).reduce((acc, value) => {
          return acc + (value.minLength ?? 0);
        }, 0)
      : undefined
  );
  /**Получить замаскированную строку */
  const getMaskedString = () => {
    return opts.date.value
      ? modifyDate(opts.date.value).formatByInputType(opts.type)
      : "";
  };

  /**Плейсхолдер на основании типа */
  const placeholder = computed(() => {
    switch (opts.type) {
      case DatepickerTypeEnum.Date:
        return "ДД.ММ.ГГГГ";
      case DatepickerTypeEnum.DateTime:
        return "ДД.ММ.ГГГГ в 00:00";
      case DatepickerTypeEnum.Week:
        return "Не выбрано";
      case DatepickerTypeEnum.Month:
        return "Не выбран";
      case DatepickerTypeEnum.Year:
        return "ГГГГ";
      case DatepickerTypeEnum.Time:
        return "00:00";
    }
  });

  /**Внутреннее значение */
  const innerValue = ref(getMaskedString());
  /**Валидное ли внутренне значение */
  const isValidInnerValue = computed(() => {
    if (!innerValue.value?.trim()) return true;

    if (
      unMasklength.value !== undefined &&
      innerValue.value.replace(/\D/g, "").length !== unMasklength.value
    ) {
      console.log(unMasklength.value);
      return false;
    }
    try {
      const date = modifyDate(
        opts.date.value ?? new Date()
      ).parseStringByInputType(innerValue.value, opts.type);

      return date && !isNaN(date.getTime());
    } catch (e) {
      return false;
    }
  });
  /**Подсветить ошибку */
  const isErrorBorder = ref(false);

  /**Отслеживание date */
  watch(
    () => opts.date.value,
    () => {
      const value = getMaskedString();
      if (innerValue.value !== value) {
        innerValue.value = value;
        isErrorBorder.value = false;
      }
    },
    { deep: true }
  );

  /**
   * * При событии acceptMask
   * @param event
   */
  const onAccept = (event: CustomEvent) => {
    const maskRef = event.detail;
    innerValue.value = maskRef.value;
  };
  /**
   * * При событии completeMask
   * @param event
   */
  const onComplete = (event: CustomEvent) => {
    const maskRef = event.detail;
    const date = modifyDate(
      opts.date.value ?? new Date()
    ).parseStringByInputType(maskRef.unmaskedValue, opts.type);
    if (!isNaN(date.getTime())) {
      opts.setDate(date);
      isErrorBorder.value = false;
    }
  };

  /**
   * * При событии blur
   */
  const onBlur = () => {
    isErrorBorder.value = !isValidInnerValue.value;
    if (!innerValue.value?.trim()) {
      opts.setDate(undefined);
    }
  };

  return {
    /**Опции маски */
    maskOptions,
    /**Внутреннее значение */
    innerValue,
    /**Плейсхолдер на основании типа */
    placeholder,
    /**Подсветить ошибку */
    isErrorBorder,
    /**Валидное ли внутренне значение */
    isValidInnerValue,
    /**При событии acceptMask */
    onAccept,
    /**При событии completeMask */
    onComplete,
    /**При событии blur */
    onBlur,
  };
};
